import { useCallback, useMemo } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import type {
  FormDescriptor,
  FormFieldDescriptor,
  ActionDescriptor,
  SubmissionState,
  FormSubmissionResult,
  FormSubmissionContext,
  FormErrorDescriptor,
} from '@finance/contracts';
import {
  isResolvedActionExecutable,
  type ActionExecutionContext,
} from '../actions/action-executor';
import { useEvaluationContext } from '../conditions/use-evaluation-context';
import type { ResolvedFieldState } from './field-state';
import { buildValidationRules, isFieldRequired } from './validation-registry';
import { getCommandFields, type ResolvedAction } from './action-resolution';
import { useFormFieldState } from './use-form-field-state';
import { useFormActions } from './use-form-actions';
import { useFormSubmission } from './use-form-submission';
import {
  financialCollectionFailure,
  validateFinancialCollections,
} from './financial-collection-validation';

export interface FormRuntimeResult {
  control: UseFormReturn['control'];
  register: UseFormReturn['register'];
  handleSubmit: UseFormReturn['handleSubmit'];
  formState: UseFormReturn['formState'];
  watch: UseFormReturn['watch'];
  reset: UseFormReturn['reset'];
  setValue: UseFormReturn['setValue'];
  getValues: UseFormReturn['getValues'];
  trigger: UseFormReturn['trigger'];
  unregister: UseFormReturn['unregister'];
  clearErrors: UseFormReturn['clearErrors'];

  visibleFields: FormFieldDescriptor[];
  fieldStates: Map<string, ResolvedFieldState>;
  formValues: Record<string, unknown>;
  defaultValues: Record<string, unknown>;
  isDirty: boolean;
  isViewMode: boolean;

  resolvedActions: ResolvedAction[];
  visibleActions: ResolvedAction[];
  resolvedSections: Map<string, boolean>;
  submitAction: (actionId: string, context?: ActionExecutionContext) => Promise<void>;
  handleAction: (action: ActionDescriptor, context?: ActionExecutionContext) => Promise<void>;

  submissionState: SubmissionState;
  submissionErrors: {
    fieldErrors: Record<string, FormErrorDescriptor>;
    globalErrors: FormErrorDescriptor[];
    submissionError: FormErrorDescriptor | null;
    conflict: { currentVersion?: string } | null;
  };
  clearConflict: () => void;
}

export interface UseFormRuntimeOptions {
  contract: FormDescriptor;
  onSubmit?: (
    commandId: string,
    values: Record<string, unknown>,
    context: FormSubmissionContext,
  ) => Promise<FormSubmissionResult>;
  onAction?: (
    action: ActionDescriptor,
    context?: ActionExecutionContext,
  ) => void | Promise<void>;
  pending?: boolean;
  serverErrors?: FormErrorDescriptor[] | Record<string, string> | null;
  initialValues?: Record<string, unknown>;
  loadingInitialValues?: boolean;
  recordVersion?: number;
}

function getDefaultValues(descriptor: FormDescriptor): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};
  for (const field of descriptor.fields) {
    if (field.value?.defaultValue !== undefined) {
      defaults[field.name] = field.value.defaultValue;
    } else if (field.fieldType === 'checkbox' || field.fieldType === 'switch') {
      defaults[field.name] = false;
    } else if (field.fieldType === 'multiselect') {
      defaults[field.name] = [];
    } else {
      defaults[field.name] = '';
    }
  }
  return defaults;
}

function toRhfValidationMode(validateWhen?: string): 'onBlur' | 'onChange' | 'onSubmit' {
  switch (validateWhen) {
    case 'change':
      return 'onChange';
    case 'submit':
      return 'onSubmit';
    case 'blur':
    default:
      return 'onBlur';
  }
}

export function useFormRuntime(options: UseFormRuntimeOptions): FormRuntimeResult {
  const {
    contract,
    onSubmit,
    onAction,
    pending = false,
    serverErrors,
    initialValues,
    loadingInitialValues,
    recordVersion,
  } = options;
  const evalCtx = useEvaluationContext();

  const defaultValues = useMemo(() => getDefaultValues(contract), [contract]);

  const formMethods = useForm({
    mode: toRhfValidationMode(contract.validation?.validateWhen),
    reValidateMode: 'onBlur',
    defaultValues,
    shouldUnregister: false,
  });

  const {
    control,
    register,
    handleSubmit,
    formState,
    watch,
    reset,
    setValue,
    getValues,
    trigger,
    unregister,
    clearErrors,
    setError,
  } = formMethods;

  const { formValuesWithIds, fieldStates, visibleFields, resolvedSections } = useFormFieldState({
    contract,
    control,
    getValues,
    setValue,
    unregister,
    clearErrors,
    evalCtx,
    pending,
  });

  const { resolvedActions, visibleActions } = useFormActions({
    contract,
    formValuesWithIds,
    evalCtx,
    pending,
    isDirty: formState.isDirty,
    isValid: formState.isValid,
  });

  const validatedOnSubmit = useCallback(
    async (
      commandId: string,
      values: Record<string, unknown>,
      context: FormSubmissionContext,
    ): Promise<FormSubmissionResult> => {
      const financialError = validateFinancialCollections(contract, values);
      if (financialError) return financialCollectionFailure(financialError);
      if (!onSubmit) return { status: 'failed' };
      return onSubmit(commandId, values, context);
    },
    [contract, onSubmit],
  );

  const submission = useFormSubmission({
    contract,
    formMethods: { trigger, getValues, setError, clearErrors, reset },
    resolvedActions,
    fieldStates,
    onSubmit: onSubmit ? validatedOnSubmit : undefined,
    onAction,
    serverErrors,
    initialValues,
    recordVersion,
    defaultValues,
    loadingInitialValues,
    formValuesWithIds,
  });

  const submitAction = useCallback(
    async (actionId: string, context?: ActionExecutionContext) => {
      const resolved = resolvedActions.find(candidate => candidate.action.id === actionId);
      if (!isResolvedActionExecutable(resolved)) return;
      await submission.submitAction(actionId, context);
    },
    [resolvedActions, submission.submitAction],
  );

  const handleAction = useCallback(
    async (action: ActionDescriptor, context?: ActionExecutionContext) => {
      const resolved = resolvedActions.find(candidate => candidate.action.id === action.id);
      if (!isResolvedActionExecutable(resolved)) return;
      await submission.handleAction(action, context);
    },
    [resolvedActions, submission.handleAction],
  );

  const isDirty = formState.isDirty;
  const isViewMode = contract.mode === 'view';

  return {
    control,
    register,
    handleSubmit,
    formState,
    watch,
    reset,
    setValue,
    getValues,
    trigger,
    unregister,
    clearErrors,

    visibleFields,
    fieldStates,
    formValues: formValuesWithIds,
    defaultValues,
    isDirty,
    isViewMode,

    resolvedActions,
    visibleActions,
    resolvedSections,
    submitAction,
    handleAction,

    submissionState: submission.submissionState,
    submissionErrors: submission.submissionErrors,
    clearConflict: submission.clearConflict,
  };
}

export { buildValidationRules, getCommandFields, isFieldRequired };
